import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import React from 'react';

const Banner = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.error.main,
  color: theme.palette.error.contrastText,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '42px',
  width: '100%',
  padding: theme.spacing(0, 2),
  position: 'sticky',
  top: 0,
  zIndex: theme.zIndex.drawer + 2,
}));
const Title = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));
const CloseButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.error.contrastText,
}));

export enum AlertType {
  Info = 'info',
  Warning = 'warning',
  Error = 'error',
  Success = 'success',
}

interface SystemNotificationBannerProps {
  message: string;
  type?: AlertType | null;
  onClose?: () => void;
}

const AlertTypeColors = {
  [AlertType.Info]: '#2f86eb',
  [AlertType.Warning]: '#ffb300',
  [AlertType.Error]: '#e53935',
  [AlertType.Success]: '#43a047',
} as const;

export const SystemNotificationBanner: React.FC<Readonly<SystemNotificationBannerProps>> = ({
  message,
  type = AlertType.Info,
  onClose,
}) => {
  React.useEffect(() => {
    if (!type && onClose) {
      onClose();
    }
  }, [type, onClose]);

  if (!type) {
    return null;
  }

  return (
    <Banner style={{ backgroundColor: AlertTypeColors[type] }}>
      <Title>
        {type === AlertType.Success && <CheckCircleOutlineIcon />}
        {type === AlertType.Info && <InfoOutlineIcon />}
        {type === AlertType.Error && <ErrorOutlineIcon />}
        {type === AlertType.Warning && <WarningAmberIcon />}
        <Typography variant="subtitle2">
          {message}
        </Typography>
      </Title>
      {onClose && (
        <Tooltip title="Dismiss">
          <CloseButton
            edge="end"
            aria-label="Dismiss notification"
            onClick={onClose}
          >
            <CloseIcon />
          </CloseButton>
        </Tooltip>
      )}
    </Banner>
  );
};
